/**
 * The sky-view lookup table: the whole sky, baked to 64×32 texels, on the CPU.
 *
 * ## Why a table rather than a shader
 *
 * The scattering integral in `model.ts` is a raymarch with a nested table lookup per step.
 * Evaluated per pixel it is perfectly affordable on a discrete GPU and distinctly not affordable
 * on the integrated GPU the 60 fps floor is written against — the sky is a *background*, it
 * covers most of the screen whenever the aircraft is not pointed at the ground, and it would be
 * competing for fill rate with the clouds, which need every microsecond they can get.
 *
 * But the sky is also an extremely *smooth* function. Radiance varies slowly and monotonically
 * with view direction almost everywhere; the only sharp feature in the whole hemisphere is the
 * sun's disc, and the disc is drawn analytically on top rather than sampled. So a 64×32 table
 * with bilinear filtering is visually identical to a per-pixel evaluation, and reduces the sky's
 * per-pixel cost to a single texture fetch — call it 0.05 ms instead of 1.5 ms.
 *
 * ## Why it is amortised
 *
 * The table depends on the sun's position and on the viewer's altitude, so it has to be rebuilt
 * when either changes. A full rebuild is ~5 ms of JavaScript, which is a visible hitch if it
 * lands in one frame, and dragging the time-of-day slider would land one in *every* frame.
 *
 * So {@link SkyViewTable.updateFrame} rebuilds a fixed number of rows per frame and swaps only
 * when the last row is done. The cost is bounded at a few tenths of a millisecond per frame, it
 * is zero when nothing is moving (the common case in level flight), and because the swap is
 * atomic the sky never shows a half-updated table with a seam across it.
 *
 * ## The parameterisation
 *
 *   - **u**: azimuth *relative to the sun*, 0 = looking at the sun, 1 = looking away. The
 *     atmosphere is symmetric about the sun's vertical plane, so half the sky is redundant and
 *     the table stores one half at double the resolution.
 *   - **v**: view zenith, with a square-root mapping that concentrates rows near the horizon.
 *     Linear spacing spends most of its rows on the empty blue overhead and resolves the
 *     horizon band — where the sunset lives — with about two.
 *
 * `updateFrame(alpha)`: yes — {@link SkyViewTable.updateFrame} does the amortised rebuild.
 * Allocation-free after construction.
 */

import { DataTexture, DataUtils, HalfFloatType, LinearFilter, RGBAFormat, ClampToEdgeWrapping } from 'three';
import {
  EARTH_ATMOSPHERE,
  MultipleScatteringTable,
  TransmittanceTable,
  rgb,
  skyRadiance,
  type AtmosphereParams,
  type Rgb,
} from './model.js';

export interface SkyViewTableOptions {
  params?: AtmosphereParams;
  /** Azimuth samples. */
  width?: number;
  /** Zenith samples. */
  height?: number;
  /** Rows rebuilt per frame. The whole of the amortisation policy, in one number. */
  rowsPerFrame?: number;
  /** Raymarch steps per texel. */
  steps?: number;
}

const scratchRadiance = /* #__PURE__ */ rgb();

export class SkyViewTable {
  readonly width: number;
  readonly height: number;
  /** Upload this. RGBA half-float: HDR values, linear filtering, core in WebGL2 and WebGPU. */
  readonly texture: DataTexture;

  readonly transmittance: TransmittanceTable;
  readonly multipleScattering: MultipleScatteringTable;

  private readonly params: AtmosphereParams;
  private readonly rowsPerFrame: number;
  private readonly steps: number;
  /** The buffer being written; swapped into the texture when a rebuild completes. */
  private readonly scratchData: Uint16Array;
  private readonly liveData: Uint16Array;

  /** Sun elevation the *in-progress* build is for [rad]. Frozen for the whole build. */
  private buildSunMu = 1;
  private buildHeightM = 0;
  /** Next row to rebuild; `height` means idle. */
  private nextRow: number;
  /** What the live table represents, so we can tell when it has gone stale. */
  private liveSunMu = Number.NaN;
  private liveHeightM = Number.NaN;
  /** What has been requested but not yet started. */
  private requestedSunMu = 1;
  private requestedHeightM = 0;

  private rebuildCountValue = 0;

  constructor(options: SkyViewTableOptions = {}) {
    this.params = options.params ?? EARTH_ATMOSPHERE;
    this.width = options.width ?? 64;
    this.height = options.height ?? 32;
    this.rowsPerFrame = options.rowsPerFrame ?? 6;
    this.steps = options.steps ?? 24;

    this.transmittance = new TransmittanceTable(this.params);
    this.multipleScattering = new MultipleScatteringTable(this.params, this.transmittance);

    const texels = this.width * this.height * 4;
    this.scratchData = new Uint16Array(texels);
    this.liveData = new Uint16Array(texels);

    this.texture = new DataTexture(this.liveData, this.width, this.height, RGBAFormat, HalfFloatType);
    this.texture.name = 'sky-view-lut';
    this.texture.minFilter = LinearFilter;
    this.texture.magFilter = LinearFilter;
    this.texture.wrapS = ClampToEdgeWrapping;
    this.texture.wrapT = ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;

    // Build the first table synchronously: the alternative is a black sky for the first handful
    // of frames, which is exactly the sort of thing that ends up in a screenshot.
    this.nextRow = this.height;
    this.request(1, 0);
    this.rebuildNow();
  }

  /** Completed rebuilds since construction. The overlay reports it; the tests assert on it. */
  get rebuildCount(): number {
    return this.rebuildCountValue;
  }

  /** True while rows are still being filled. */
  get isBuilding(): boolean {
    return this.nextRow < this.height;
  }

  /**
   * Ask for a table at this sun elevation and viewer altitude.
   *
   * @param sunMu    cosine of the sun's zenith angle: 1 overhead, 0 on the horizon, negative at night.
   * @param heightM  viewer altitude above sea level [m].
   */
  request(sunMu: number, heightM: number): void {
    this.requestedSunMu = Math.min(1, Math.max(-1, sunMu));
    this.requestedHeightM = Math.max(0, heightM);
  }

  /**
   * Has what we were asked for drifted far enough from what we have to be worth a rebuild?
   *
   * The thresholds are the point of this function. `mu` is a cosine, so a fixed threshold on it
   * is a *tighter* angular threshold near the horizon (where `d(mu)/d(elevation)` is largest)
   * than overhead — which is exactly the right bias, because that is where the colours change
   * fastest. 0.004 in `mu` is about a quarter of a degree of sun elevation at the horizon, well
   * below what the eye can catch between two frames.
   */
  private isStale(): boolean {
    if (Number.isNaN(this.liveSunMu)) return true;
    if (Math.abs(this.requestedSunMu - this.liveSunMu) > 0.004) return true;
    // Altitude matters far less, and only over kilometres — the sky from 500 ft and from
    // 2000 ft is the same sky.
    return Math.abs(this.requestedHeightM - this.liveHeightM) > 750;
  }

  /**
   * Advance the rebuild. Call once per frame.
   *
   * @returns milliseconds of work is not returned — the caller times it. This returns whether a
   *          rebuild completed this frame, which the overlay uses.
   */
  updateFrame(): boolean {
    if (!this.isBuilding) {
      if (!this.isStale()) return false;
      // Freeze the target for the duration of the build. Chasing a moving sun mid-build would
      // put rows from different sun positions in the same table, and the seam between them is
      // a visible horizontal band.
      this.buildSunMu = this.requestedSunMu;
      this.buildHeightM = this.requestedHeightM;
      this.nextRow = 0;
    }

    const last = Math.min(this.height, this.nextRow + this.rowsPerFrame);
    for (let y = this.nextRow; y < last; y++) this.buildRow(y);
    this.nextRow = last;

    if (this.nextRow < this.height) return false;
    this.commit();
    return true;
  }

  /** Build the whole table right now, ignoring the per-frame budget. Start-up and tests. */
  rebuildNow(): void {
    this.buildSunMu = this.requestedSunMu;
    this.buildHeightM = this.requestedHeightM;
    for (let y = 0; y < this.height; y++) this.buildRow(y);
    this.nextRow = this.height;
    this.commit();
  }

  private commit(): void {
    this.liveData.set(this.scratchData);
    this.texture.needsUpdate = true;
    this.liveSunMu = this.buildSunMu;
    this.liveHeightM = this.buildHeightM;
    this.rebuildCountValue++;
  }

  private buildRow(y: number): void {
    const sunMu = this.buildSunMu;
    const heightM = this.buildHeightM;
    // The sun's zenith *angle*, so we can build a view direction with a known angle to it.
    const sunZenith = Math.acos(Math.min(1, Math.max(-1, sunMu)));
    const sinSunZenith = Math.sin(sunZenith);

    const viewZenith = zenithFromRow(y, this.height);
    const viewMu = Math.cos(viewZenith);
    const sinViewZenith = Math.sin(viewZenith);

    for (let x = 0; x < this.width; x++) {
      // Azimuth from the sun's vertical plane: 0 = toward the sun, π = directly away.
      const azimuth = (x / (this.width - 1)) * Math.PI;
      // Angle between the view ray and the sun, from the spherical law of cosines.
      const cosTheta = Math.min(
        1,
        Math.max(-1, viewMu * sunMu + sinViewZenith * sinSunZenith * Math.cos(azimuth)),
      );

      skyRadiance(
        heightM,
        viewMu,
        sunMu,
        cosTheta,
        {
          params: this.params,
          transmittance: this.transmittance,
          multipleScattering: this.multipleScattering,
          steps: this.steps,
        },
        scratchRadiance,
      );

      const index = (y * this.width + x) * 4;
      this.scratchData[index] = DataUtils.toHalfFloat(scratchRadiance.r);
      this.scratchData[index + 1] = DataUtils.toHalfFloat(scratchRadiance.g);
      this.scratchData[index + 2] = DataUtils.toHalfFloat(scratchRadiance.b);
      this.scratchData[index + 3] = ONE_HALF_FLOAT;
    }
  }

  /** Read the live table the way the shader will, for tests and for the ambient-light fit. */
  sample(viewZenithRad: number, azimuthFromSunRad: number, out: Rgb): Rgb {
    const u = (Math.abs(wrapPi(azimuthFromSunRad)) / Math.PI) * (this.width - 1);
    const v = rowFromZenith(viewZenithRad, this.height);

    const x0 = Math.max(0, Math.min(this.width - 1, Math.floor(u)));
    const x1 = Math.min(this.width - 1, x0 + 1);
    const y0 = Math.max(0, Math.min(this.height - 1, Math.floor(v)));
    const y1 = Math.min(this.height - 1, y0 + 1);
    const fx = u - x0;
    const fy = v - y0;

    const read = (x: number, y: number, c: number): number =>
      DataUtils.fromHalfFloat(this.liveData[(y * this.width + x) * 4 + c]);

    for (let c = 0; c < 3; c++) {
      const top = read(x0, y0, c) * (1 - fx) + read(x1, y0, c) * fx;
      const bottom = read(x0, y1, c) * (1 - fx) + read(x1, y1, c) * fx;
      const value = top * (1 - fy) + bottom * fy;
      if (c === 0) out.r = value;
      else if (c === 1) out.g = value;
      else out.b = value;
    }
    return out;
  }

  dispose(): void {
    this.texture.dispose();
  }
}

/** 1.0 encoded as a half-float — a constant, so the alpha write is not a function call per texel. */
const ONE_HALF_FLOAT = /* #__PURE__ */ DataUtils.toHalfFloat(1);

/**
 * Row ↔ view zenith angle, concentrated near the horizon.
 *
 * The mapping is symmetric about the horizon (`v = 0.5`) and quadratic on each side, so the
 * ten degrees either side of the horizon — sunset colours, the haze band, the point where the
 * ocean meets the sky — get roughly a third of the rows. Must stay in exact agreement with
 * `zenithToSkyUv` in `sky.ts`; the shader reads the same table.
 */
export function zenithFromRow(row: number, height: number): number {
  const v = row / (height - 1);
  const centred = (v - 0.5) * 2; // −1 (zenith) … +1 (nadir)
  const signed = centred >= 0 ? centred * centred : -(centred * centred);
  return (signed * 0.5 + 0.5) * Math.PI;
}

export function rowFromZenith(zenithRad: number, height: number): number {
  const normalised = Math.min(1, Math.max(0, zenithRad / Math.PI));
  const signed = (normalised - 0.5) * 2;
  const centred = signed >= 0 ? Math.sqrt(signed) : -Math.sqrt(-signed);
  return (centred * 0.5 + 0.5) * (height - 1);
}

function wrapPi(angle: number): number {
  const twoPi = Math.PI * 2;
  let a = angle % twoPi;
  if (a > Math.PI) a -= twoPi;
  if (a < -Math.PI) a += twoPi;
  return a;
}
